/**
 * Primitivos visuais da modal de captura — réplica de `qr-base.css` do mockup
 * (`.kv`, `.info-box`, `.pts`, `.done-head`, `.mdl-head`). Modal é SEMPRE tema
 * claro (o `Modal` shared já faz o override), então as cores são as do mockup.
 */
import React from 'react';

import { Icon, type IconName } from '@/shared/icons';

export const CaptureKv: React.FC<{ rows: Array<{ k: string; v: React.ReactNode; mono?: boolean; testId?: string }> }> = ({ rows }) => (
  <div className="rounded-[10px] overflow-hidden" style={{ border: '1px solid #eef1f5' }} data-testid="capture-kv">
    {rows.map((row, i) => (
      <div
        key={row.k}
        className="flex items-center justify-between gap-3 text-[13px]"
        style={{ padding: '10px 14px', borderTop: i === 0 ? undefined : '1px solid #f1f5f9' }}
        data-testid={row.testId}
      >
        <span style={{ color: '#64748b' }}>{row.k}</span>
        <span className="text-right min-w-0" style={{ color: '#1e293b', fontWeight: row.mono ? 500 : 600, fontVariantNumeric: row.mono ? 'tabular-nums' : undefined }}>{row.v}</span>
      </div>
    ))}
  </div>
);

const INFO_TONES = {
  slate: { background: '#f8fafc', color: '#475569', border: '1px solid #eef1f5' },
  amber: { background: '#fffbeb', color: '#b45309', border: 'none' },
  blue: { background: '#eff6ff', color: '#1d4ed8', border: 'none' },
} as const;

export const CaptureInfoBox: React.FC<{ tone: keyof typeof INFO_TONES; icon: IconName; children: React.ReactNode; className?: string; testId?: string; dataVariant?: string }> = ({ tone, icon, children, className = '', testId, dataVariant }) => (
  <div className={`flex gap-2 items-start rounded-[9px] text-xs font-medium ${className}`} style={{ padding: '10px 12px', ...INFO_TONES[tone] }} data-testid={testId} data-variant={dataVariant}>
    <Icon name={icon} size={14} className="flex-none mt-px" />
    <span className="leading-[1.5]">{children}</span>
  </div>
);

/** `.pts` — pílula roxa "+1 ponto · lead captado e confirmado". */
export const CapturePointsPill: React.FC<{ children: React.ReactNode; testId?: string; dataVariant?: string }> = ({ children, testId, dataVariant }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full text-xs font-bold" style={{ background: '#f3e8ff', color: '#6d28d9', padding: '3px 10px' }} data-testid={testId} data-variant={dataVariant}>
    <Icon name="star" size={13} />
    {children}
  </span>
);

/** `.done-head` — círculo + título + subtítulo ("Etapa 1 concluída"). */
export const CaptureDoneHead: React.FC<{ tone: 'green' | 'wa'; icon: IconName; title: string; children: React.ReactNode; testId?: string; dataVariant?: string }> = ({ tone, icon, title, children, testId, dataVariant }) => (
  <div className="flex items-center gap-3 mb-3.5" data-testid={testId} data-variant={dataVariant}>
    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-none" style={tone === 'wa' ? { background: '#e7f9ee', color: '#1aa34a' } : { background: '#ecfdf5', color: '#059669' }}>
      <Icon name={icon} size={20} />
    </div>
    <div className="min-w-0">
      <h4 className="m-0 text-[15px] font-bold tracking-[-0.02em]" style={{ color: '#1e293b' }}>{title}</h4>
      <p className="m-0 mt-0.5 text-xs" style={{ color: '#64748b' }}>{children}</p>
    </div>
  </div>
);

/** `.mdl-head` — ícone quadrado + título + subtítulo. Usa o slot `children` do `ModalHeader`. */
export const CaptureModalHeader: React.FC<{ icon: IconName; tone?: 'purple' | 'green'; title: string; subtitle?: string }> = ({ icon, tone = 'purple', title, subtitle }) => (
  <div className="flex items-center gap-[11px] min-w-0">
    <div className="w-9 h-9 rounded-[9px] flex items-center justify-center flex-none" style={tone === 'green' ? { background: '#ecfdf5', color: '#059669' } : { background: '#f3e8ff', color: '#7c3aed' }}>
      <Icon name={icon} size={18} />
    </div>
    <div className="min-w-0">
      <div className="font-bold text-base tracking-[-0.02em] truncate" style={{ color: '#1e293b' }} data-testid="capture-title">{title}</div>
      {subtitle && <div className="text-xs truncate" style={{ color: '#64748b' }} data-testid="capture-subtitle">{subtitle}</div>}
    </div>
  </div>
);
